// lib/axios.ts
import axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  type AxiosError,
} from 'axios';
import { NO_AUTH_REQUIRED_API_ROUTES, API_ROUTES, LOGOUT_ERROR_CODES } from '@/constants';
import { useAuthStore } from '@/stores/useAuthStore';

// ============================================
// Instance
// ============================================

export const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/',
});

// Routes that should skip auth (including dynamic poll routes for public access)
const isNoAuthRoute = (url: string | undefined): boolean => {
    if (!url) return false;
    // Exact matches
    if ((NO_AUTH_REQUIRED_API_ROUTES as readonly string[]).includes(url)) return true;
    // GET requests to polls list and detail are public
    if (url.startsWith('/api/polls/') && !url.includes('/vote') && !url.includes('/comments')) return true;
    return false;
};

// ============================================
// Request Interceptor
// ============================================

api.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        // Public GET endpoints don't need auth
        if (isNoAuthRoute(config.url) && config.method === 'get') {
            // Still attach token if available for user_vote data
            const token = await useAuthStore.getState().getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }

        if ((NO_AUTH_REQUIRED_API_ROUTES as readonly string[]).includes(config.url || '')) {
            return config;
        }

        const token = await useAuthStore.getState().getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// ============================================
// Response Interceptor
// ============================================

api.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        const { response, config: originalRequest } = error;

        if (!response || !originalRequest) {
            return Promise.reject(error);
        }

        const { status } = response;
        const { logOut, getAccessToken } = useAuthStore.getState();

        // 503 - Maintenance
        if (status === 503) {
            return Promise.reject(error);
        }

        // 401 - Unauthorized
        if (status === 401) {
            const errorCode = (response.data as Record<string, unknown>)?.code as string || '';

            if (LOGOUT_ERROR_CODES.includes(errorCode)) {
                logOut();
                return Promise.reject(error);
            }

            const shouldAttemptRefresh =
                !(NO_AUTH_REQUIRED_API_ROUTES as readonly string[]).includes(originalRequest.url || '') &&
                originalRequest.url !== API_ROUTES.refresh;

            if (shouldAttemptRefresh) {
                try {
                    const newToken = await getAccessToken();
                    if (newToken) {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return api(originalRequest);
                    }
                } catch {
                    logOut();
                }
            }
        }

        return Promise.reject(error);
    }
);
